// src/App.js
import React from 'react';
import { ThemeProvider } from 'styled-components';
import { LazyMotion, domAnimation } from 'framer-motion';
import { Routes, Route, useLocation } from 'react-router-dom';

import GlobalStyles from './styles/GlobalStyles';
import ScrollProgress from './components/ScrollProgress';
import LoadingScreen from './components/LoadingScreen';

import {
  HeroSection,
  WhoWeAreSection,
  WhatWeDoSection,
  ServicesSection,
  TestimonialsSection,
  CVSessionsSection,
  FounderStorySection,
  GetInTouchSection,
  Header,
  Footer
} from './components';

import PrivacyPolicy from './pages/PrivacyPolicy';
import HowWeWorkSection from './components/HowWeWork';

const theme = {
  colors: {
    primary: '#3b82f6',
    secondary: '#9ca3af',
    accent: '#f59e0b',
    background: '#000000',
    text: '#e2e8f0',
    light: '#1a202c',
    dark: '#0f172a',
    success: '#10b981',
    error: '#ef4444',
    warning: '#f59e0b',
    info: '#3b82f6',
    card: '#1a202c',
    border: '#2d3748'
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1280px'
  },
  spacing: { xs: '0.5rem', sm: '1rem', md: '1.5rem', lg: '2rem', xl: '3rem', xxl: '5rem' },
  borderRadius: { sm: '0.375rem', md: '0.5rem', lg: '0.75rem', xl: '1rem', full: '9999px' },
  shadows: { /* your shadows */ },
  gradients: { /* your gradients */ }
};

function App() {
  const [isLoading, setIsLoading] = React.useState(true);
  const location = useLocation();

  const handleLoadingComplete = () => {
    setIsLoading(false);
    document.body.classList.remove('js-loading');
  };

  React.useEffect(() => {
    const img = new Image();
    img.src = '/SSDLogo.svg';
    
    // If we're not on homepage, skip loading screen
    if (location.pathname !== '/') {
      setIsLoading(false);
    }
  }, [location.pathname]);

  const showLoadingScreen = isLoading && location.pathname === '/';

  return (
    <LazyMotion features={domAnimation}>
      <ThemeProvider theme={theme}>
        <GlobalStyles />

        {/* Branded loading screen – only on first visit to home */}
        {showLoadingScreen && <LoadingScreen onLoadingComplete={handleLoadingComplete} />}

        {/* Show routes immediately if not on homepage, or after loading completes */}
        {(!isLoading || location.pathname !== '/') && (
          <Routes>
            {/* HOMEPAGE – your original animation */}
            <Route
              path="/"
              element={
                <div className="page-load-animation" style={{ background: '#000000' }}>
                  <ScrollProgress />
                  <Header />
                  <HeroSection />
                  <WhoWeAreSection />
                  <HowWeWorkSection />
                  <WhatWeDoSection />
                  <ServicesSection />
                  <TestimonialsSection />
                  <CVSessionsSection />
                  <FounderStorySection />
                  <GetInTouchSection />
                  <Footer />
                </div>
              }
            />

            {/* PRIVACY POLICY – instant, fully visible */}
            <Route
              path="/privacy-policy"
              element={
                <>
                  <ScrollProgress />
                  <Header />
                  <PrivacyPolicy />
                  <Footer />
                </>
              }
            />
          </Routes>
        )}
      </ThemeProvider>
    </LazyMotion>
  );
}

export default App;